import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { Divider, Drawer, IconButton, SvgIcon, Typography } from '@mui/material';
import { DateConfigurationPage } from './components/DateConfigurationPage';
import { MyViewConfigurationPage } from './components/MyViewConfigurationPage';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { RoleBasedAccessProvider } from 'components/RBAC';
import { ROLES } from 'constant/PermissonConstant';
import { DataImportConfigurationPage } from './components/DataImportConfigurationPage';
import { ComponentsConfigurationPage } from './components/ComponentsConfigurationPage';

const closeIconStyle = { color: '#98A2AE' };

interface ConfigurationPageProps {
  visible?: boolean;
  onClose?: () => void;
}

enum CONFIGURATION_MENU_ID {
  DATE_CONFIG = 'DATE_CONFIG',
  PEOPLE_CONFIG = 'PEOPLE_CONFIG',
  IMPORT_CONFIG = 'IMPORT_CONFIG',
  COMPONENT_CONFIG = 'COMPONENT_CONFIG',
}

const CONFIGURATION_MENU = [
  {
    id: CONFIGURATION_MENU_ID.PEOPLE_CONFIG,
    name: 'My View',
    subtitle: 'User dashboards & navigation',
    icon: GroupOutlinedIcon,
    roles: [ROLES.BUILDER, ROLES.PACKAGER, ROLES.SUPPORT, ROLES.TESTER],
  },
  {
    id: CONFIGURATION_MENU_ID.COMPONENT_CONFIG,
    name: 'Components',
    subtitle: 'Component configuration & management',
    icon: WidgetsOutlinedIcon,
    roles: [ROLES.BUILDER, ROLES.PACKAGER, ROLES.SUPPORT, ROLES.TESTER],
  },
  {
    id: CONFIGURATION_MENU_ID.DATE_CONFIG,
    name: 'Date',
    subtitle: 'Financial year & periods',
    icon: CalendarMonthOutlinedIcon,
    roles: [ROLES.MANAGER, ROLES.BUILDER, ROLES.SUPPORT, ROLES.TESTER, ROLES.PACKAGER],
  },
  {
    id: CONFIGURATION_MENU_ID.IMPORT_CONFIG,
    name: 'Data Import',
    subtitle: 'Import validation rules',
    icon: DriveFolderUploadOutlinedIcon,
    roles: [ROLES.BUILDER, ROLES.PACKAGER, ROLES.SUPPORT, ROLES.TESTER],
  },
];

function getPageContent(menuId: CONFIGURATION_MENU_ID | null) {
  switch (menuId) {
    case CONFIGURATION_MENU_ID.DATE_CONFIG:
      return <DateConfigurationPage />;
    case CONFIGURATION_MENU_ID.PEOPLE_CONFIG:
      return <MyViewConfigurationPage />;
    case CONFIGURATION_MENU_ID.IMPORT_CONFIG:
      return <DataImportConfigurationPage />;
    case CONFIGURATION_MENU_ID.COMPONENT_CONFIG:
      return <ComponentsConfigurationPage />;
    default:
      return null;
  }
}

export default function ConfigurationPage(props: Readonly<ConfigurationPageProps>) {
  const { visible = false, onClose } = props;
  const [selectedMenu, setSelectedMenu] = useState<CONFIGURATION_MENU_ID | null>(CONFIGURATION_MENU[0].id);

  const handleOnCloseModal = () => {
    setSelectedMenu(CONFIGURATION_MENU[0].id);
    onClose && onClose();
  };

  const activeMenuLabel = CONFIGURATION_MENU.find((m) => m.id === selectedMenu)?.name ?? '';

  return (
    <Drawer
      anchor='right'
      open={visible}
      onClose={handleOnCloseModal}
      PaperProps={{ sx: { width: '88%', maxWidth: 1100 } }}
    >
      <Stack direction='column' height='100%' px={2} py={2}>
        {/* Header */}
        <Stack>
          <Stack justifyContent='space-between' direction='row' alignItems='center'>
            <Stack>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                Tenant Settings
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {activeMenuLabel}
              </Typography>
            </Stack>
            <IconButton onClick={handleOnCloseModal} size='small'>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
        </Stack>
        <Divider sx={{ mb: 1, mt: 1 }} />

        {/* Body */}
        <Stack direction='row' gap={1} flex={1} minHeight={0}>
          {/* Sidebar */}
          <Box width={230} bgcolor='#f9f9f9' flexShrink={0} overflow='auto' borderRight='1px solid #F0F0F0'>
            <List disablePadding>
              {CONFIGURATION_MENU.map((item) => (
                <RoleBasedAccessProvider key={item.id} allowedRoles={item.roles}>
                  <ListItem disablePadding sx={{ position: 'relative' }}>
                    {item.id === selectedMenu && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          width: 4,
                          height: '100%',
                          backgroundColor: '#4856b7',
                          borderRadius: '0 2px 2px 0',
                        }}
                      />
                    )}
                    <ListItemButton
                      onClick={() => setSelectedMenu(item.id)}
                      sx={{
                        pl: 2.5,
                        py: 1.2,
                        backgroundColor: item.id === selectedMenu ? '#EEF0FC' : 'transparent',
                        '&:hover': {
                          backgroundColor: item.id === selectedMenu ? '#EEF0FC' : 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <SvgIcon
                          component={item.icon}
                          fontSize='small'
                          color={item.id === selectedMenu ? 'primary' : 'inherit'}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        secondary={item.subtitle}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: item.id === selectedMenu ? 700 : 500,
                          color: item.id === selectedMenu ? '#3B4797' : 'inherit',
                        }}
                        secondaryTypographyProps={{ fontSize: 11 }}
                      />
                    </ListItemButton>
                  </ListItem>
                </RoleBasedAccessProvider>
              ))}
            </List>
          </Box>

          {/* Content area */}
          <Box flex={1} overflow='auto'>
            {getPageContent(selectedMenu)}
          </Box>
        </Stack>
      </Stack>
    </Drawer>
  );
}
